import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Autocomplete,
  Pagination,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InsightsIcon from "@mui/icons-material/Insights";
import GradingIcon from "@mui/icons-material/Grading";
import SaveIcon from "@mui/icons-material/Save";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import QuizIcon from "@mui/icons-material/Quiz";
import SchoolIcon from "@mui/icons-material/School";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";

import { useNavigate } from "react-router-dom";
import { API_URL } from "./config";

const AILessonTeacherHubPage = () => {
  //const navigate = useNavigate();
  const token = localStorage.getItem("auth") || localStorage.getItem("token") || "";

  const ORANGE = "#F07828";
  const ORANGE_2 = "#ed7f35ff";
  const BLUE = "#1565C0";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.04)";
  const ROW_HOVER = "rgba(25,118,210,0.08)";

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const pageWrapSx = { p: 2, minHeight: "100vh", backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: ORANGE,
    "&:hover": { backgroundColor: ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": {
      borderBottom: "1px solid rgba(25,118,210,0.10)",
      verticalAlign: "top",
    },
  });

  const [loading, setLoading] = useState(false);
  const [lessonTree, setLessonTree] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState("");
  const [selectedLessonData, setSelectedLessonData] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);

  const [selectedAttemptId, setSelectedAttemptId] = useState("");
  const [attemptData, setAttemptData] = useState(null);
  const [attemptLoading, setAttemptLoading] = useState(false);

  const [gradeDrafts, setGradeDrafts] = useState({});
  const [savingAnswerId, setSavingAnswerId] = useState("");
  const [finalizing, setFinalizing] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const [submissionFilter, setSubmissionFilter] = useState("NEEDS_REVIEW");
  const [submissionSearch, setSubmissionSearch] = useState("");
  const [answerFilter, setAnswerFilter] = useState("ALL");

  const FILTER_KEYS = ["SubCampusCode", "DegreeCode", "BranchCode", "Semester", "Section", "SubjectCombo"];
  const emptyFilters = () => Object.fromEntries(FILTER_KEYS.map((key) => [key, []]));
  const [filters, setFilters] = useState(emptyFilters);
  const [gradingFilter, setGradingFilter] = useState("ALL");
  const [lessonPage, setLessonPage] = useState(1);
  const LESSONS_PER_PAGE = 10;

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const n = (v, d = 0) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : d;
  };

  const percentage = (score, max) => {
    const m = n(max, 0);
    if (!m) return 0;
    return Math.max(0, Math.min(100, (n(score, 0) / m) * 100));
  };

  const asDate = (v) => {
    if (!v) return "-";
    try {
      return new Date(v).toLocaleString();
    } catch {
      return String(v);
    }
  };

  const getLessonRows = useMemo(() => {
    const rows = [];

    (lessonTree || []).forEach((subject) => {
      const lessons = subject.chapters || subject.lessons || [];
      lessons.forEach((lesson) => {
        rows.push({
          ...lesson,
          _SubjectCode: subject.SubjectCode || lesson.SubjectCode,
          _SubjectName: subject.SubjectName || lesson.SubjectName,
        });
      });
    });

    return rows;
  }, [lessonTree]);

  const dashboardStats = useMemo(() => {
    const rows = getLessonRows;
    return {
      lessons: rows.length,
      published: rows.filter((x) => x.ReviewStatus === "PUBLISHED").length,
      draft: rows.filter((x) => x.ReviewStatus === "DRAFT").length,
      attempts: rows.reduce(
        (s, x) => s + n(x.analyticsPreview?.TotalAttempts || x.TotalAttempts, 0),
        0
      ),
    };
  }, [getLessonRows]);

  const lessonAnalytics = selectedLessonData?.analytics || {};
  const submissions = selectedLessonData?.submissions || [];
  const questionBreakdown = selectedLessonData?.questionBreakdown || [];
  const selectedLesson = selectedLessonData?.lesson || null;
  const selectedAssignment = selectedLessonData?.assignment || null;

  const aiInsights = useMemo(() => {
    const items = [];
    const totalAttempts = n(lessonAnalytics.TotalAttempts, 0);
    const uniqueStudents = n(lessonAnalytics.UniqueStudents, 0);
    const submitted = n(lessonAnalytics.SubmittedCount, 0);
    const graded = n(lessonAnalytics.GradedCount, 0);
    const inProgress = n(lessonAnalytics.InProgressCount, 0);
    const avgCompletion = n(lessonAnalytics.AvgCompletionPercent, 0);
    const avgScorePct = percentage(
      lessonAnalytics.AvgTotalScore,
      lessonAnalytics.AvgMaxScore
    );

    if (!selectedLessonId) {
      items.push({
        type: "info",
        title: "Select a lesson",
        text: "Choose an uploaded lesson from the left panel to view quiz performance, submissions, and grading.",
      });
      return items;
    }

    if (!totalAttempts) {
      items.push({
        type: "warning",
        title: "No attempts yet",
        text: "Students have not attempted this lesson quiz yet. Ask students to open the lesson and submit the quiz.",
      });
      return items;
    }

    items.push({
      type: "info",
      title: "Participation",
      text: `${uniqueStudents || totalAttempts} student(s) have activity for this lesson. ${submitted} submitted, ${graded} graded, and ${inProgress} still in progress.`,
    });

    items.push({
      type: avgScorePct >= 70 ? "success" : avgScorePct >= 45 ? "warning" : "danger",
      title: "Overall performance",
      text: `Average quiz score is ${avgScorePct.toFixed(
        1
      )}%. Average completion is ${avgCompletion.toFixed(1)}%.`,
    });

    const weakQuestions = questionBreakdown
      .map((q) => ({
        ...q,
        pct: percentage(q.AvgFinalScore, q.AvgMaxScore),
      }))
      .filter((q) => n(q.Responses, 0) > 0 && q.pct < 60)
      .sort((a, b) => a.pct - b.pct)
      .slice(0, 3);

    const strongQuestions = questionBreakdown
      .map((q) => ({
        ...q,
        pct: percentage(q.AvgFinalScore, q.AvgMaxScore),
      }))
      .filter((q) => n(q.Responses, 0) > 0 && q.pct >= 80)
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 3);

    if (weakQuestions.length) {
      items.push({
        type: "danger",
        title: "Needs re-teaching",
        text: `Students struggled most with: ${weakQuestions
          .map((q) => `${q.QuestionId} (${q.pct.toFixed(1)}%)`)
          .join(", ")}. Add one more explanation or practice activity for these concepts.`,
      });
    }

    if (strongQuestions.length) {
      items.push({
        type: "success",
        title: "Strong areas",
        text: `Students performed well on: ${strongQuestions
          .map((q) => `${q.QuestionId} (${q.pct.toFixed(1)}%)`)
          .join(", ")}.`,
      });
    }

    if (submitted > graded) {
      items.push({
        type: "warning",
        title: "Grading pending",
        text: `${submitted - graded} submitted attempt(s) still need teacher grading or finalization.`,
      });
    }

    return items;
  }, [selectedLessonId, lessonAnalytics, questionBreakdown]);

  const filterOptions = useMemo(() => {
    const values = (key) => [...new Set(getLessonRows.map((row) => String(
      key === "SubjectCombo"
        ? `${row._SubjectCode || ""} - ${row._SubjectName || ""}`.trim()
        : row[key] ?? ""
    ).trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return Object.fromEntries(FILTER_KEYS.map((key) => [key, values(key)]));
  }, [getLessonRows]);

  const filteredLessons = useMemo(() => getLessonRows.filter((row) => {
    const matchesAcademic = FILTER_KEYS.every((key) => {
      const selected = filters[key] || [];
      const value = String(key === "SubjectCombo"
        ? `${row._SubjectCode || ""} - ${row._SubjectName || ""}`.trim()
        : row[key] ?? "").trim().toLowerCase();
      return !selected.length || selected.some((choice) => String(choice).trim().toLowerCase() === value);
    });
    if (!matchesAcademic) return false;
    const pending = Number(row.analyticsPreview?.SubmittedCount ?? row.SubmittedCount ?? 0)
      - Number(row.analyticsPreview?.GradedCount ?? row.GradedCount ?? 0);
    if (gradingFilter === "PENDING") return pending > 0;
    if (gradingFilter === "GRADED") return Number(row.analyticsPreview?.GradedCount ?? row.GradedCount ?? 0) > 0 && pending <= 0;
    return true;
  }).sort((a, b) => Number(b.LessonId || 0) - Number(a.LessonId || 0)), [getLessonRows, filters, gradingFilter]);

  const lessonPageCount = Math.max(1, Math.ceil(filteredLessons.length / LESSONS_PER_PAGE));
  const pagedLessons = filteredLessons.slice((lessonPage - 1) * LESSONS_PER_PAGE, lessonPage * LESSONS_PER_PAGE);

  const fetchLessonTree = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_URL}/api/ai-lessons/teacher/lesson-tree`, {
        headers: authHeaders,
      });
      setLessonTree(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load teacher lessons");
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonDetails = async (lessonId, preserveAttempt = false) => {
    if (!lessonId) return;

    setLessonLoading(true);
    setError("");
    if (!preserveAttempt) {
      setSelectedAttemptId("");
      setAttemptData(null);
      setGradeDrafts({});
    }

    try {
      const [detailRes, analyticsRes] = await Promise.all([
        axios.get(`${API_URL}/api/ai-lesson-delivery/teacher/lessons/${lessonId}`, {
          headers: authHeaders,
        }),
        axios.get(
          `${API_URL}/api/ai-lesson-delivery/teacher/lessons/${lessonId}/analytics`,
          { headers: authHeaders }
        ),
      ]);

      setSelectedLessonData({
        lesson: detailRes.data?.lesson || null,
        assignment: detailRes.data?.assignment || null,
        submissions: detailRes.data?.submissions || [],
        analytics: analyticsRes.data?.summary || {},
        analyticsLesson: analyticsRes.data?.lesson || null,
        statusBreakdown: analyticsRes.data?.statusBreakdown || [],
        questionBreakdown: analyticsRes.data?.questionBreakdown || [],
      });
    } catch (err) {
      setSelectedLessonData(null);
      setError(err.response?.data?.error || "Failed to load lesson details");
    } finally {
      setLessonLoading(false);
    }
  };

  const fetchAttempt = async (attemptId) => {
    if (!attemptId) return;

    setAttemptLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API_URL}/api/ai-lesson-delivery/teacher/attempts/${attemptId}`,
        { headers: authHeaders }
      );

      const answers = Array.isArray(res.data?.answers) ? res.data.answers : [];
      const drafts = {};

      answers.forEach((a) => {
        drafts[a.AnswerId] = {
          TeacherScore: a.TeacherScore ?? a.FinalScore ?? a.AutoScore ?? 0,
          FeedbackText: a.FeedbackText || "",
        };
      });

      setAttemptData(res.data || null);
      setGradeDrafts(drafts);
    } catch (err) {
      setAttemptData(null);
      setError(err.response?.data?.error || "Failed to load attempt");
    } finally {
      setAttemptLoading(false);
    }
  };

  const saveAnswerGrade = async (answer) => {
    const draft = gradeDrafts[answer.AnswerId] || {};
    const maxScore = n(answer.MaxScore, 0);
    const score = Math.max(0, Math.min(maxScore, n(draft.TeacherScore, 0)));

    setSavingAnswerId(answer.AnswerId);
    setError("");

    try {
      await axios.post(
        `${API_URL}/api/ai-lesson-delivery/teacher/answers/${answer.AnswerId}/grade`,
        {
          TeacherScore: score,
          FeedbackText: draft.FeedbackText || "",
        },
        { headers: authHeaders }
      );

      setSuccess("Grade saved");
      await fetchAttempt(answer.AttemptId);
      await fetchLessonDetails(answer.LessonId, true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save answer grade");
    } finally {
      setSavingAnswerId("");
    }
  };

  const isManual = (a) => !["MCQ", "TRUE_FALSE", "FILL_BLANK"].includes(String(a.QuestionType || "").toUpperCase());
  const isGraded = (a) => ["TEACHER_FINALIZED", "GRADED"].includes(String(a.GradingStatus || "").toUpperCase());
  const pendingManual = (attemptData?.answers || []).filter((a) => isManual(a) && !isGraded(a));
  const hasUnsaved = (a) => {
    const d = gradeDrafts[a.AnswerId];
    return d && (Number(d.TeacherScore) !== Number(a.TeacherScore ?? a.FinalScore ?? a.AutoScore ?? 0) ||
      String(d.FeedbackText || "") !== String(a.FeedbackText || ""));
  };
  const saveAllGrades = async () => {
    const answers = (attemptData?.answers || []).filter((a) => hasUnsaved(a) || (isManual(a) && !isGraded(a)));
    if (!answers.length) return setSuccess("No grades to save");
    setSavingAll(true);
    setError("");
    try {
      for (const a of answers) {
        const draft = gradeDrafts[a.AnswerId] || {};
        const score = Number(draft.TeacherScore);
        if (!Number.isFinite(score) || score < 0 || score > n(a.MaxScore)) throw new Error(`Invalid score for question ${a.QuestionId}`);
        await axios.post(`${API_URL}/api/ai-lesson-delivery/teacher/answers/${a.AnswerId}/grade`,
          { TeacherScore: score, FeedbackText: draft.FeedbackText || "" }, { headers: authHeaders });
      }
      await fetchAttempt(attemptData.attempt.AttemptId);
      await fetchLessonDetails(selectedLessonId, true);
      setSuccess(`${answers.length} answer grade(s) saved`);
    } catch (err) { setError(err.response?.data?.error || err.message || "Failed to save grades"); }
    finally { setSavingAll(false); }
  };

  const finalizeAttempt = async () => {
    const attemptId = attemptData?.attempt?.AttemptId;
    if (!attemptId) return;
    if (pendingManual.length) return setError(`Grade ${pendingManual.length} pending theory answer(s) before finalizing.`);
    if ((attemptData?.answers || []).some(hasUnsaved)) return setError("Save changed grades before finalizing.");
    if (!window.confirm("Finalize this student's marks? Confirm all answers have been reviewed.")) return;

    setFinalizing(true);
    setError("");

    try {
      await axios.post(
        `${API_URL}/api/ai-lesson-delivery/teacher/attempts/${attemptId}/finalize`,
        {},
        { headers: authHeaders }
      );

      setSuccess("Attempt finalized");
      await fetchAttempt(attemptId);
      await fetchLessonDetails(selectedLessonId, true);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to finalize attempt");
    } finally {
      setFinalizing(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchLessonTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    setLessonPage(1);
  }, [filters, gradingFilter]);

  useEffect(() => {
    if (!selectedLessonId) return;
    fetchLessonDetails(selectedLessonId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLessonId]);

  useEffect(() => {
    if (!selectedAttemptId) return;
    fetchAttempt(selectedAttemptId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAttemptId]);

  const setDraft = (answerId, key, value) => {
    setGradeDrafts((prev) => ({
      ...prev,
      [answerId]: {
        ...(prev[answerId] || {}),
        [key]: value,
      },
    }));
  };

  const answerText = (a) => {
    if (a.AnswerText) return a.AnswerText;
    if (!a.AnswerJson) return "-";
    try {
      const parsed =
        typeof a.AnswerJson === "string" ? JSON.parse(a.AnswerJson) : a.AnswerJson;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return String(a.AnswerJson);
    }
  };

  const insightColor = (type) => {
    if (type === "success") return "#2E7D32";
    if (type === "warning") return "#EF6C00";
    if (type === "danger") return "#C62828";
    return BLUE;
  };

  const StatCard = ({ icon, label, value, helper }) => (
    <Paper
      elevation={0}
      sx={{
        ...surfaceSx,
        p: 1.5,
        height: "100%",
        backgroundColor: "rgba(25,118,210,0.025)",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{ color: ORANGE, display: "flex" }}>{icon}</Box>
        <Box>
          <Typography sx={{ color: "text.secondary", fontSize: "0.78rem", fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography sx={{ color: BLUE, fontSize: "1.25rem", fontWeight: 900 }}>
            {value}
          </Typography>
          {helper && (
            <Typography sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
              {helper}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );

  const renderLessonList = () => (
    <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
      <Box
        sx={{
          px: 2,
          py: 1.25,
          backgroundColor: "rgba(25,118,210,0.05)",
          borderBottom: `1px solid ${BORDER}`,
        }}
      >
        <Typography sx={{ fontWeight: 900, color: BLUE }}>Uploaded Lessons</Typography>
        <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
          Newest lesson ID first. Select a lesson to open its grading queue.
        </Typography>
      </Box>

      <Box sx={{ maxHeight: "calc(100vh - 360px)", overflow: "auto" }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={28} />
            <Typography sx={{ mt: 1, color: "text.secondary" }}>Loading lessons...</Typography>
          </Box>
        ) : !filteredLessons.length ? (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ color: "text.secondary" }}>No uploaded lessons found.</Typography>
          </Box>
        ) : (
          <Stack spacing={0}>
            {pagedLessons.map((lesson, idx) => {
              const active = Number(selectedLessonId) === Number(lesson.LessonId);
              return (
                <Box
                  key={lesson.LessonId}
                  onClick={() => setSelectedLessonId(lesson.LessonId)}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    borderBottom: "1px solid rgba(25,118,210,0.10)",
                    backgroundColor: active
                      ? "rgba(240,120,40,0.11)"
                      : idx % 2 === 0
                      ? ROW_ALT
                      : "#fff",
                    "&:hover": { backgroundColor: active ? "rgba(240,120,40,0.14)" : ROW_HOVER },
                  }}
                >
                  <Stack direction="row" spacing={1} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: active ? ORANGE : "#1f2937" }}>
                        {lesson.ChapterTitle || lesson.LessonTitle || "Untitled Lesson"}
                      </Typography>
                      <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                        {lesson._SubjectCode || lesson.SubjectCode}{" "}
                        {lesson._SubjectName ? `- ${lesson._SubjectName}` : ""}
                      </Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                        Sem {lesson.Semester || "-"} | Sec{" "}
                        {lesson.Section || "-"}
                      </Typography>

                      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1 }}>
                        <Chip size="small" label={lesson.ReviewStatus || "DRAFT"} />
                        <Chip size="small" label={`Lesson #${lesson.LessonId}`} />
                        {lesson.analyticsPreview?.TotalAttempts != null && (
                          <Chip
                            size="small"
                            label={`Attempts ${lesson.analyticsPreview.TotalAttempts}`}
                          />
                        )}
                      </Stack>
                    </Box>

                    <IconButton size="small" sx={{ color: active ? ORANGE : BLUE }}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
      {filteredLessons.length > LESSONS_PER_PAGE && (
        <Stack alignItems="center" spacing={0.5} sx={{ py: 1.5, borderTop: `1px solid ${BORDER}` }}>
          <Typography variant="caption" color="text.secondary">
            {filteredLessons.length} lessons · Page {lessonPage} of {lessonPageCount}
          </Typography>
          <Pagination size="small" count={lessonPageCount} page={Math.min(lessonPage, lessonPageCount)}
            onChange={(_, page) => setLessonPage(page)} color="primary" />
        </Stack>
      )}
    </Paper>
  );

  const renderLessonOverview = () => {
    if (!selectedLessonId) {
      return (
        <Paper elevation={0} sx={{ ...surfaceSx, p: 3, textAlign: "center" }}>
          <SchoolIcon sx={{ fontSize: 42, color: ORANGE, mb: 1 }} />
          <Typography sx={{ fontWeight: 900, color: BLUE }}>
            Select an uploaded lesson
          </Typography>
          <Typography sx={{ color: "text.secondary" }}>
            You will see quiz performance, AI insights, student submissions, and grading tools here.
          </Typography>
        </Paper>
      );
    }

    if (lessonLoading) {
      return (
        <Paper elevation={0} sx={{ ...surfaceSx, p: 3, textAlign: "center" }}>
          <CircularProgress size={30} />
          <Typography sx={{ mt: 1, color: "text.secondary" }}>Loading lesson dashboard...</Typography>
        </Paper>
      );
    }

    return (
      <Stack spacing={2}>
        <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            flexWrap="wrap"
          >
            <Box>
              <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "1.05rem" }}>
                {selectedLesson?.LessonTitle || selectedLesson?.ChapterTitle || "Lesson"}
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.88rem" }}>
                {selectedLesson?.SubjectCode} | {selectedLesson?.ChapterTitle || "-"} | ClassID:{" "}
                {selectedLesson?.ClassID || "-"}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip size="small" label={selectedLesson?.ReviewStatus || "-"} />
                <Chip size="small" label={`Lesson #${selectedLesson?.LessonId || selectedLessonId}`} />
                <Chip size="small" label={`Assignment: ${selectedAssignment?.AssignmentTitle || "Not found"}`} />
              </Stack>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
             

              <Button
                  variant="outlined"
                  sx={outlinedBtnSx}
                  startIcon={<AssignmentTurnedInIcon />}
                  onClick={() =>
                    window.open(
                      `/AILessonTeacherPage/${selectedLessonId}`,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  Full Teacher View
              </Button>

              <Button
                variant="outlined"
                sx={outlinedBtnSx}
                startIcon={<RefreshIcon />}
                onClick={() => fetchLessonDetails(selectedLessonId)}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={1.5}>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={<QuizIcon />}
              label="Total Attempts"
              value={n(lessonAnalytics.TotalAttempts, 0)}
              helper={`${n(lessonAnalytics.UniqueStudents, 0)} unique students`}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={<AssignmentTurnedInIcon />}
              label="Submitted"
              value={n(lessonAnalytics.SubmittedCount, 0)}
              helper={`${n(lessonAnalytics.GradedCount, 0)} graded`}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={<AnalyticsIcon />}
              label="Avg Completion"
              value={`${n(lessonAnalytics.AvgCompletionPercent, 0).toFixed(1)}%`}
              helper={`${n(lessonAnalytics.InProgressCount, 0)} in progress`}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard
              icon={<InsightsIcon />}
              label="Avg Score"
              value={`${percentage(
                lessonAnalytics.AvgTotalScore,
                lessonAnalytics.AvgMaxScore
              ).toFixed(1)}%`}
              helper={`${n(lessonAnalytics.AvgTotalScore, 0).toFixed(2)} / ${n(
                lessonAnalytics.AvgMaxScore,
                0
              ).toFixed(2)}`}
            />
          </Grid>
        </Grid>

        <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <InsightsIcon sx={{ color: ORANGE }} />
            <Typography sx={{ fontWeight: 900, color: BLUE }}>AI Quiz Insights</Typography>
          </Stack>

          <Grid container spacing={1.25}>
            {aiInsights.map((item, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${BORDER}`,
                    backgroundColor: "rgba(25,118,210,0.025)",
                    height: "100%",
                  }}
                >
                  <Typography sx={{ fontWeight: 900, color: insightColor(item.type) }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
                    {item.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {renderQuestionAnalytics()}
        {renderSubmissions()}
        {renderAttemptGrading()}
      </Stack>
    );
  };

  const renderQuestionAnalytics = () => (
    <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
      <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
        Question-wise Quiz Analytics
      </Typography>

      {!questionBreakdown.length ? (
        <Typography sx={{ color: "text.secondary" }}>
          No question-wise analytics available yet.
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={theadRowSx}>
                <TableCell>Question</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Responses</TableCell>
                <TableCell align="right">Avg Score</TableCell>
                <TableCell>Performance</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {questionBreakdown.map((q, idx) => {
                const p = percentage(q.AvgFinalScore, q.AvgMaxScore);
                return (
                  <TableRow key={`${q.QuestionId}-${idx}`} sx={zebraRowSx(idx)}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 900 }}>{q.QuestionId}</Typography>
                      <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>
                        {q.QuestionText || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>{q.QuestionType || "-"}</TableCell>
                    <TableCell align="right">{n(q.Responses, 0)}</TableCell>
                    <TableCell align="right">
                      {n(q.AvgFinalScore, 0).toFixed(2)} / {n(q.AvgMaxScore, 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      <LinearProgress
                        variant="determinate"
                        value={p}
                        sx={{ height: 8, borderRadius: 10, mb: 0.5 }}
                      />
                      <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                        {p.toFixed(1)}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  const visibleSubmissions = submissions.filter((s) => {
    const status = String(s.Status || "").toUpperCase();
    const matches = `${s.StudentID || ""} ${s.StudentKey || ""} ${s.AttemptId || ""}`.toLowerCase().includes(submissionSearch.toLowerCase());
    return matches && (submissionFilter === "ALL" || (submissionFilter === "NEEDS_REVIEW" && status === "SUBMITTED") || status === submissionFilter);
  });

  const renderSubmissions = () => (
    <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
      <Typography sx={{ fontWeight: 900, color: BLUE, mb: 1 }}>
        Student Submissions
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField size="small" fullWidth label="Find student / attempt" value={submissionSearch} onChange={(e) => setSubmissionSearch(e.target.value)} />
        <TextField size="small" select label="Review queue" value={submissionFilter} onChange={(e) => setSubmissionFilter(e.target.value)} sx={{ minWidth: 205 }}>
          <MenuItem value="NEEDS_REVIEW">Awaiting grading</MenuItem><MenuItem value="ALL">All attempts</MenuItem>
          <MenuItem value="SUBMITTED">Submitted</MenuItem><MenuItem value="GRADED">Graded</MenuItem><MenuItem value="IN_PROGRESS">In progress</MenuItem>
        </TextField>
        <Chip color="warning" label={`${submissions.filter(s => String(s.Status).toUpperCase() === "SUBMITTED").length} to review`} />
      </Stack>
      {!visibleSubmissions.length ? (
        <Typography sx={{ color: "text.secondary" }}>
          No matching submissions. Change the review filter to see other attempts.
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={theadRowSx}>
                <TableCell>Student</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Completion</TableCell>
                <TableCell align="right">Score</TableCell>
                <TableCell>Started</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleSubmissions.map((s, idx) => {
                const active = Number(selectedAttemptId) === Number(s.AttemptId);
                return (
                  <TableRow
                    key={s.AttemptId}
                    sx={{
                      ...zebraRowSx(idx),
                      backgroundColor: active ? "rgba(240,120,40,0.10)" : zebraRowSx(idx).backgroundColor,
                    }}
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 900 }}>
                        {s.StudentID || s.StudentKey}
                      </Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                        Attempt #{s.AttemptId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={s.Status || "-"} />
                    </TableCell>
                    <TableCell align="right">
                      {n(s.CompletionPercent, 0).toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">
                      {n(s.TotalScore, 0).toFixed(2)} / {n(s.MaxScore, 0).toFixed(2)}
                    </TableCell>
                    <TableCell>{asDate(s.StartedAt)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant={active ? "contained" : "outlined"}
                        sx={active ? primaryBtnSx : outlinedBtnSx}
                        startIcon={<GradingIcon />}
                        onClick={() => setSelectedAttemptId(s.AttemptId)}
                      >
                        Grade
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  const renderAttemptGrading = () => {
    if (!selectedAttemptId) return null;

    const attempt = attemptData?.attempt;
    const answers = attemptData?.answers || [];

    return (
      <Paper
        elevation={0}
        sx={{
          ...surfaceSx,
          p: 2,
          borderColor: "rgba(240,120,40,0.45)",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          sx={{ mb: 1.5 }}
        >
          <Box>
            <Typography sx={{ fontWeight: 900, color: ORANGE }}>
              Grade Student Attempt
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.88rem" }}>
              {attempt
                ? `${attempt.StudentID || attempt.StudentKey} | Attempt #${attempt.AttemptId} | ${attempt.Status}`
                : "Loading attempt..."}
            </Typography>
          </Box>

          <Button variant="outlined" sx={outlinedBtnSx} startIcon={<SaveIcon />} disabled={savingAll || attemptLoading || !answers.length} onClick={saveAllGrades}>
            {savingAll ? "Saving..." : "Save all grades"}
          </Button>
          <Button
            variant="contained"
            sx={primaryBtnSx}
            startIcon={<DoneAllIcon />}
            disabled={!attempt || finalizing || savingAll || pendingManual.length > 0 || answers.some(hasUnsaved)}
            onClick={finalizeAttempt}
          >
            {finalizing ? "Finalizing..." : "Finalize Attempt"}
          </Button>
        </Stack>

        {attempt && <Alert severity={pendingManual.length ? "warning" : "info"} sx={{ mb: 2 }}>
          {`${answers.length} recorded answers · ${pendingManual.length} theory answers awaiting review · ${answers.filter(hasUnsaved).length} unsaved changes`}
          {pendingManual.length ? " — grade the pending theory answers before finalizing." : " — review marks and finalize when ready."}
        </Alert>}
        <TextField select size="small" label="Show answers" value={answerFilter} onChange={(e) => setAnswerFilter(e.target.value)} sx={{ minWidth: 230, mb: 2 }}>
          <MenuItem value="ALL">All answers</MenuItem><MenuItem value="PENDING">Pending theory only</MenuItem><MenuItem value="THEORY">Theory answers</MenuItem><MenuItem value="OBJECTIVE">Objective answers</MenuItem>
        </TextField>
        {attemptLoading ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={28} />
            <Typography sx={{ mt: 1, color: "text.secondary" }}>Loading answers...</Typography>
          </Box>
        ) : !answers.length ? (
          <Typography sx={{ color: "text.secondary" }}>No answers found for this attempt.</Typography>
        ) : (
          <Stack spacing={1.25}>
            {answers.filter((a) => answerFilter === "ALL" || (answerFilter === "PENDING" && isManual(a) && !isGraded(a)) || (answerFilter === "THEORY" && isManual(a)) || (answerFilter === "OBJECTIVE" && !isManual(a))).map((a, idx) => {
              const objective = ["MCQ", "TRUE_FALSE", "FILL_BLANK"].includes(a.QuestionType);
              const draft = gradeDrafts[a.AnswerId] || {
                TeacherScore: 0,
                FeedbackText: "",
              };

              return (
                <Paper
                  key={a.AnswerId}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${BORDER}`,
                    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#fff",
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                      <Chip size="small" label={a.QuestionType || "QUESTION"} />
                      <Chip size="small" label={`Max ${n(a.MaxScore, 0)}`} />
                      <Chip size="small" label={`Auto ${n(a.AutoScore, 0)}`} />
                      <Chip size="small" label={`Teacher ${n(a.TeacherScore, 0)}`} />
                      <Chip size="small" label={`Final ${n(a.FinalScore, 0)}`} />
                      <Chip size="small" color={isGraded(a) ? "success" : isManual(a) ? "warning" : "info"} label={a.GradingStatus || "PENDING"} />
                      {hasUnsaved(a) && <Chip size="small" color="warning" label="Unsaved" />}
                    </Stack>

                    <Typography sx={{ fontWeight: 900 }}>
                      {idx + 1}. {a.QuestionText || a.QuestionId}
                    </Typography>

                    <Grid container spacing={1.25}>
                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: `1px solid ${BORDER}`,
                            backgroundColor: "#fff",
                            height: "100%",
                          }}
                        >
                          <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "0.82rem" }}>
                            Student Answer
                          </Typography>
                          <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>
                            {answerText(a)}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: `1px solid ${BORDER}`,
                            backgroundColor: "#fff",
                            height: "100%",
                          }}
                        >
                          <Typography sx={{ fontWeight: 900, color: BLUE, fontSize: "0.82rem" }}>
                            Correct / Expected Answer
                          </Typography>
                          <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "0.9rem" }}>
                            {a.CorrectAnswer || "-"}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <Stack spacing={1}>
                          <TextField
                            label={objective ? "Score" : "Teacher Score"}
                            type="number"
                            size="small"
                            sx={inputSx}
                            value={draft.TeacherScore}
                            inputProps={{
                              min: 0,
                              max: n(a.MaxScore, 0),
                              step: 0.5,
                            }}
                            onChange={(e) =>
                              setDraft(a.AnswerId, "TeacherScore", e.target.value)
                            }
                          />

                          <TextField
                            label="Feedback"
                            size="small"
                            sx={inputSx}
                            multiline
                            minRows={2}
                            value={draft.FeedbackText}
                            onChange={(e) =>
                              setDraft(a.AnswerId, "FeedbackText", e.target.value)
                            }
                          />

                          <Button
                            variant="contained"
                            sx={primaryBtnSx}
                            startIcon={<SaveIcon />}
                            disabled={savingAll || String(savingAnswerId) === String(a.AnswerId)}
                            onClick={() => saveAnswerGrade(a)}
                          >
                            {String(savingAnswerId) === String(a.AnswerId)
                              ? "Saving..."
                              : "Save Grade"}
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.15rem", fontWeight: 900 }}>
              AI Lesson Teacher Hub
            </Typography>
            <Typography sx={{ opacity: 0.92, fontSize: "0.86rem" }}>
              View uploaded lessons, monitor quiz performance, grade submissions, and act on AI insights.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            sx={{
              ...outlinedBtnSx,
              color: "white",
              borderColor: "rgba(255,255,255,0.75)",
              "&:hover": {
                borderColor: "white",
                backgroundColor: "rgba(255,255,255,0.12)",
              },
            }}
            startIcon={<RefreshIcon />}
            onClick={fetchLessonTree}
          >
            Refresh
          </Button>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          <Snackbar
            open={!!success}
            autoHideDuration={3000}
            onClose={() => setSuccess("")}
          >
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              {success}
            </Alert>
          </Snackbar>

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<SchoolIcon />}
                label="Uploaded Lessons"
                value={dashboardStats.lessons}
                helper="In your teacher scope"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<VisibilityIcon />}
                label="Published"
                value={dashboardStats.published}
                helper={`${dashboardStats.draft} draft`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<QuizIcon />}
                label="Total Attempts"
                value={dashboardStats.attempts}
                helper="Across listed lessons"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                icon={<GradingIcon />}
                label="Selected"
                value={selectedLessonId || "-"}
                helper="Lesson ID"
              />
            </Grid>
          </Grid>

          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2, borderLeft: `5px solid ${ORANGE}` }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}
              justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 900, color: BLUE }}>Find lessons to grade</Typography>
                <Typography variant="body2" color="text.secondary">
                  Filter by class details, then select a lesson and review student submissions.
                </Typography>
              </Box>
              <Button variant="outlined" sx={outlinedBtnSx} onClick={() => {
                setFilters(emptyFilters()); setGradingFilter("ALL"); setLessonPage(1);
              }}>Clear filters</Button>
            </Stack>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)", lg: "repeat(7, 1fr)" }, gap: 1.25 }}>
              {[
                ["SubCampusCode", "SubCampus"], ["DegreeCode", "Degree"],
                ["BranchCode", "Branch"], ["Semester", "Semester"],
                ["Section", "Section"], ["SubjectCombo", "Subject Code + Subject Name"],
              ].map(([key, label]) => (
                <Autocomplete key={key} multiple disableCloseOnSelect filterSelectedOptions
                  options={filterOptions[key] || []} value={filters[key] || []}
                  onChange={(_, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
                  renderInput={(params) => <TextField {...params} label={label} size="small" sx={inputSx} />}
                />
              ))}
              <TextField select size="small" label="Grading" sx={inputSx}
                value={gradingFilter} onChange={(e) => setGradingFilter(e.target.value)}>
                <MenuItem value="ALL">All lessons</MenuItem>
                <MenuItem value="PENDING">To be graded</MenuItem>
                <MenuItem value="GRADED">Graded</MenuItem>
              </TextField>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
              Showing {filteredLessons.length} of {getLessonRows.length} lessons · Newest lesson ID first
            </Typography>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4} lg={3}>
              {renderLessonList()}
            </Grid>

            <Grid item xs={12} md={8} lg={9}>
              {renderLessonOverview()}
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AILessonTeacherHubPage;